export function numberValue(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

export function clamp(value, minimum, maximum) {
  return Math.min(Math.max(numberValue(value), minimum), maximum);
}

export function costPerGram(profile) {
  const spoolWeight = numberValue(profile?.spoolWeightGrams);
  return spoolWeight > 0 ? numberValue(profile?.spoolPrice) / spoolWeight : 0;
}

export function scaleBatchProduction(inputs, nextQuantity, basisQuantity = inputs?.quantity) {
  const fromQuantity = Math.max(1, Math.floor(numberValue(basisQuantity, 1)));
  const toQuantity = Math.max(1, Math.floor(numberValue(nextQuantity, fromQuantity)));
  const ratio = toQuantity / fromQuantity;
  const totalMinutes = (Math.max(0, numberValue(inputs?.printHours)) * 60)
    + clamp(inputs?.printMinutes, 0, 59);
  const scaledMinutes = Math.max(0, Math.round(totalMinutes * ratio));
  return {
    quantity: toQuantity,
    filamentGrams: Math.round(Math.max(0, numberValue(inputs?.filamentGrams)) * ratio * 10) / 10,
    printHours: Math.floor(scaledMinutes / 60),
    printMinutes: scaledMinutes % 60,
  };
}

export function calculateEstimate(profile, inputs) {
  const quantity = Math.max(1, Math.floor(numberValue(inputs.quantity, 1)));
  const printHours = Math.max(0, numberValue(inputs.printHours));
  const printMinutes = clamp(inputs.printMinutes, 0, 59);
  const totalPrintHours = printHours + (printMinutes / 60);
  const filamentGrams = Math.max(0, numberValue(inputs.filamentGrams));
  const averagePowerWatts = Math.max(0, numberValue(inputs.averagePowerWatts));
  const laborMinutes = Math.max(0, numberValue(inputs.laborMinutes));
  const additionalCost = Math.max(0, numberValue(inputs.additionalCost));
  const targetMargin = clamp(inputs.targetMargin, 0, 94.99);

  const materialCost = filamentGrams * costPerGram(profile);
  const electricityKwh = (averagePowerWatts / 1000) * totalPrintHours;
  const electricityCost = electricityKwh * numberValue(profile?.electricityRate);
  const machineCost = totalPrintHours * numberValue(profile?.machineRate);
  const laborCost = (laborMinutes / 60) * numberValue(profile?.laborRate);
  const fixedCost = numberValue(profile?.fixedCost) + additionalCost;

  const riskEligibleCost = materialCost + electricityCost + machineCost + laborCost;
  const failureAllowance = riskEligibleCost * (numberValue(profile?.wastePercent) / 100);
  const totalBatchCost = riskEligibleCost + failureAllowance + fixedCost;
  const costPerPiece = totalBatchCost / quantity;
  const marginDecimal = targetMargin / 100;
  const recommendedPricePerPiece = marginDecimal < 1
    ? costPerPiece / (1 - marginDecimal)
    : costPerPiece;
  const recommendedBatchPrice = recommendedPricePerPiece * quantity;
  const profitPerPiece = recommendedPricePerPiece - costPerPiece;
  const totalProfit = recommendedBatchPrice - totalBatchCost;

  return {
    quantity,
    totalPrintHours,
    filamentGrams,
    averagePowerWatts,
    laborMinutes,
    targetMargin,
    materialCost,
    electricityKwh,
    electricityCost,
    machineCost,
    laborCost,
    fixedCost,
    failureAllowance,
    totalBatchCost,
    costPerPiece,
    recommendedPricePerPiece,
    recommendedBatchPrice,
    profitPerPiece,
    totalProfit,
  };
}
